// The tile pictures (design 10 §3, T15; design 11 §5): how each sprite is drawn and how a family of them is
// packed into one sheet. Pure drawing on any 2D context, so the same code runs in the sprite worker
// (ui/sprites.worker.ts) and, where a worker cannot draw, on the page.
//
// A tile is a box: 20.5 mm across, 28 mm long, 16 mm thick (game/place, TILE_MM), an ivory layer over a
// coloured back. The board is seen from a camera leaning back θ from straight above, without perspective, so
// the picture of a box depends only on θ and which way the box lies, never on where it is: a point (x, y, z)
// of the table, z up from the felt, is on the screen at (x, b − (b − y)·cos θ − z·sin θ), b the board's near
// edge, about which the board's own surface turns by the same θ (ui/Table). A box shows its top, shortened by
// cos θ, and its near side, its height times sin θ; nothing else of it is visible. So each face is drawn
// lying in four orientations, and the back lying and standing, for the chosen θ, and a tile on the table is
// shown in its orientation, never turned at rest: turning a picture of a box breaks it (owner, 2026-10-13).
// The own row stays upright on the screen, as a hand is held.
//
// The faces come from the face sheet (scripts/atlas.mjs, design 11 §4); no canvas filter is used (darkening
// is a translucent fill), which a worker or Safari may lack.

import { DEPTH, H } from "../game/place";
import manifest from "../assets/atlas/faces.json";
import { THEME_ID, colour, css, rgbOf } from "../theme";

export const BACK = "Back";
const FACE_NAMES: readonly string[] = manifest.names;
const ART_SOURCE = manifest.source; // the tile art's version: a change draws every sheet again

const FACE_LAYER = 0.62; // the ivory layer's share of the thickness; the coloured back is the rest
const RADIUS = 0.13; // a tile's corners, in tile widths (Front.svg)
const SIDE = 0.8; // the sides take less of the light from above than the top
const LIGHT_X = 0.02; // the light comes from the top left (design 15 §1.1): a shadow falls this far to the right, in tile widths
const IVORY = rgbOf("tile-face"); // the face's own colour (Front.svg, in the theme's)
const BACK_RGB = rgbOf("tile-back"); // the back's (Back.svg, in the theme's)
const UMBRA = rgbOf("umbra"); // a shadow's

/** What a sprite shows: a tile in the own row, upright on the screen; lying on the table; standing on it. */
export type Kind = "hand" | "flat" | "stand";
/** The sprites of one sheet: the own row's, or the table's. */
export type Family = "hand" | "table";

/** A sprite's size in tile widths, and its anchor within it: the tile's centre for the own row, the centre
 *  of the box's footprint on the felt for the table. */
export interface Box {
  width: number;
  height: number;
  cx: number;
  cy: number;
}

/** How a tile is shown: as it is, a discard straight from the draw (a little darker, as most clients show
 *  tsumogiri), or dimmed (a tile that cannot be played now): the black laid over it. */
export type Look = "plain" | "dark" | "dim";
export const LOOKS: Record<Look, number> = { plain: 0, dark: 0.18, dim: 0.45 };

// The own row's margins: room for its thin edge and its shadow.
const HAND_PAD = { x: 0.2, top: 0.15, bottom: 0.35 };
const HAND_EDGE = 0.06;
// The table's: room for the shadow.
const PAD = { x: 0.12, top: 0.08, bottom: 0.22 };

/** The box's extent in tile widths: across the screen, down it, and up from the felt. */
function solid(kind: Kind, o: number): { xs: number; ys: number; zs: number } {
  const odd = o % 2 === 1;
  if (kind === "stand") return { xs: odd ? DEPTH : 1, ys: odd ? 1 : DEPTH, zs: H };
  return { xs: odd ? H : 1, ys: odd ? 1 : H, zs: DEPTH };
}

const rad = (deg: number) => (deg * Math.PI) / 180;

/** A sprite of `kind` in orientation `o` (its face turned o quarter turns clockwise), the table seen at `deg`. */
export function spriteBox(kind: Kind, o: number, deg: number): Box {
  if (kind === "hand") {
    const fw = o % 2 ? H : 1;
    const fh = o % 2 ? 1 : H;
    return { width: fw + 2 * HAND_PAD.x, height: fh + HAND_PAD.top + HAND_PAD.bottom, cx: HAND_PAD.x + fw / 2, cy: HAND_PAD.top + fh / 2 };
  }
  const { xs, ys, zs } = solid(kind, o);
  const c = Math.cos(rad(deg));
  const s = Math.sin(rad(deg));
  return { width: xs + 2 * PAD.x, height: PAD.top + zs * s + ys * c + PAD.bottom, cx: PAD.x + xs / 2, cy: PAD.top + zs * s + (ys * c) / 2 };
}

/** The top of a box lying in orientation `o` as on the screen, in tile widths: its size, and how far its
 *  centre is above the anchor (the last discard's ring is drawn round it). */
export function topFace(o: number, deg: number): { width: number; height: number; up: number } {
  const { xs, ys, zs } = solid("flat", o);
  return { width: xs, height: ys * Math.cos(rad(deg)), up: zs * Math.sin(rad(deg)) };
}

/** Where face `name` is in the face sheet of width `w`. */
export function cell(w: number, name: string): { x: number; y: number; w: number; h: number } {
  const s = manifest.sheets[String(w) as keyof typeof manifest.sheets];
  let i = FACE_NAMES.indexOf(name);
  if (i < 0) i = Math.max(0, FACE_NAMES.indexOf("Blank"));
  return { x: (i % manifest.cols) * (s.w + manifest.gutter), y: Math.floor(i / manifest.cols) * (s.h + manifest.gutter), w: s.w, h: s.h };
}

/** The face sheets' widths, smallest first. */
export const SHEET_WIDTHS = Object.keys(manifest.sheets).map(Number).sort((a, b) => a - b);

type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

/** The face sheet, as the drawing reads it. */
export interface Faces {
  sheet: ImageBitmap;
  w: number; // its faces' width
}

const shade = (rgb: readonly number[], k: number) => css(rgb.map((v) => v * k));

function roundRect(ctx: Ctx, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}

/** The face (or the teal back) at width `w`, centred on the origin, turned `o` quarter turns. */
function drawFace(ctx: Ctx, w: number, f: Faces, name: string, o: number): void {
  const c = cell(f.w, name);
  ctx.rotate((o * Math.PI) / 2);
  ctx.drawImage(f.sheet, c.x, c.y, c.w, c.h, -w / 2, -(w * H) / 2, w, w * H);
}

/** An own row's tile at width `w` in orientation `o`, upright on the screen: its shadow, a thin edge under
 *  it, then the face, turned o quarter turns. */
function drawHand(ctx: Ctx, w: number, f: Faces, name: string, o: number): void {
  const h = w * H;
  const fw = o % 2 ? h : w;
  const fh = o % 2 ? w : h;
  const x = HAND_PAD.x * w;
  const y = HAND_PAD.top * w;
  const r = RADIUS * w;
  ctx.save();
  ctx.shadowColor = css(UMBRA, 0.45);
  ctx.shadowBlur = 0.14 * w;
  ctx.shadowOffsetX = LIGHT_X * w;
  ctx.shadowOffsetY = (HAND_EDGE + 0.02) * w;
  ctx.fillStyle = colour("tile-edge");
  roundRect(ctx, x, y + HAND_EDGE * w, fw, fh, r);
  ctx.fill();
  ctx.restore();
  ctx.save();
  roundRect(ctx, x, y, fw, fh, r);
  ctx.clip();
  ctx.translate(x + fw / 2, y + fh / 2);
  drawFace(ctx, w, f, name, o);
  ctx.restore();
}

/** The box at width `w` as `kind` in orientation `o`, seen at `deg`, in a cell `bh` pixels tall: its contact
 *  shadow, its near side (the ivory and the coloured layer, or the whole face a standing tile turns to the
 *  camera), then its top, shortened. `name` is the face lying up, or BACK lying face down; a standing tile
 *  is always its back. */
function drawSolid(ctx: Ctx, w: number, bh: number, f: Faces, name: string, kind: Kind, o: number, deg: number): void {
  const box = solid(kind, o);
  const xs = box.xs * w;
  const ys = box.ys * w;
  const zs = box.zs * w;
  const c = Math.cos(rad(deg));
  const s = Math.sin(rad(deg));
  const x0 = PAD.x * w;
  const y0 = PAD.top * w;
  const r = Math.min(RADIUS * w, 0.3 * Math.min(xs, ys));
  // The box between heights zb and zt as on the screen: its footprint at zt swept down to zb (the corners
  // are rounded on the felt, so shortened by cos θ on the screen).
  const slab = (zb: number, zt: number) => {
    ctx.save();
    ctx.translate(x0, y0 + (zs - zt) * s);
    ctx.scale(1, c);
    roundRect(ctx, 0, 0, xs, ys + ((zt - zb) * s) / c, r);
    ctx.restore();
  };
  const top = () => slab(zs, zs);
  const back = BACK_RGB;
  // The shadow on the felt, under the whole box.
  ctx.save();
  ctx.shadowColor = css(UMBRA, 0.42);
  ctx.shadowBlur = 0.12 * w;
  ctx.shadowOffsetX = LIGHT_X * w;
  ctx.shadowOffsetY = 0.03 * w;
  ctx.fillStyle = shade(back, 0.7);
  slab(0, zs);
  ctx.fill();
  ctx.restore();
  if (kind === "flat") {
    // Lying: the near side is the two layers, the upper one the top's own (ivory face up, coloured face down),
    // drawn from the felt up, each over what it hides of the one under it.
    const down = name === BACK;
    const split = (down ? FACE_LAYER : 1 - FACE_LAYER) * zs;
    ctx.fillStyle = shade(down ? IVORY : back, SIDE);
    slab(0, split);
    ctx.fill();
    ctx.fillStyle = shade(down ? back : IVORY, SIDE);
    slab(split, zs);
    ctx.fill();
    ctx.save();
    top();
    ctx.clip();
    ctx.translate(x0 + xs / 2, y0 + (ys * c) / 2);
    ctx.scale(1, c);
    drawFace(ctx, w, f, name, o);
    ctx.restore();
    return;
  }
  // Standing, its face turned to its owner: o 0 the camera, 1 the left, 2 away, 3 the right. Its near side.
  ctx.save();
  slab(0, zs);
  ctx.clip();
  if (o % 2 === 0) {
    // A whole face to the camera, its height shortened by sin θ, in the side's light: the back (o 2), or
    // the blank front (o 0).
    if (o === 2) {
      ctx.save();
      ctx.translate(x0 + xs / 2, y0 + ys * c + (zs * s) / 2);
      ctx.scale(1, Math.max(s, 0.001));
      drawFace(ctx, w, f, BACK, 0);
      ctx.restore();
    } else {
      ctx.fillStyle = shade(IVORY, 1);
      ctx.fillRect(x0, y0, xs, bh);
    }
    ctx.fillStyle = css(UMBRA, Number((1 - SIDE - 0.05).toFixed(2)));
    ctx.fillRect(0, 0, x0 * 2 + xs, bh);
  } else {
    // Its edge to the camera: the two layers side by side, the ivory towards its owner.
    const ivoryLeft = o === 1;
    const cut = x0 + (ivoryLeft ? FACE_LAYER : 1 - FACE_LAYER) * xs;
    ctx.fillStyle = shade(ivoryLeft ? IVORY : back, SIDE);
    ctx.fillRect(x0, 0, cut - x0, bh);
    ctx.fillStyle = shade(ivoryLeft ? back : IVORY, SIDE);
    ctx.fillRect(cut, 0, x0 + xs - cut, bh);
  }
  ctx.restore();
  // Its top: the tile's upper edge, the two layers again, the ivory towards its owner.
  ctx.save();
  top();
  ctx.clip();
  if (o % 2 === 0) {
    const ivoryFar = o === 2;
    const cut = y0 + (ivoryFar ? FACE_LAYER : 1 - FACE_LAYER) * ys * c;
    ctx.fillStyle = shade(ivoryFar ? IVORY : back, 1);
    ctx.fillRect(x0, y0, xs, cut - y0);
    ctx.fillStyle = shade(ivoryFar ? back : IVORY, 1);
    ctx.fillRect(x0, cut, xs, y0 + ys * c - cut);
  } else {
    const ivoryLeft = o === 1;
    const cut = x0 + (ivoryLeft ? FACE_LAYER : 1 - FACE_LAYER) * xs;
    ctx.fillStyle = shade(ivoryLeft ? IVORY : back, 1);
    ctx.fillRect(x0, y0, cut - x0, ys * c);
    ctx.fillStyle = shade(ivoryLeft ? back : IVORY, 1);
    ctx.fillRect(cut, y0, x0 + xs - cut, ys * c);
  }
  ctx.restore();
}

/** One sprite's place in a sheet: its key (`${kind}:${name}:${o}`) and its rectangle, in pixels. */
export type Item = [key: string, x: number, y: number, w: number, h: number];

/** The sprites of a family at width `w` (device pixels) and angle `deg`, packed in rows: every face upright
 *  and sideways for the own row; every face lying in four orientations and the back standing too for the
 *  table. */
export function layout(family: Family, w: number, deg: number): { width: number; height: number; items: Item[] } {
  const wanted: [Kind, string, number][] = [];
  for (const name of FACE_NAMES) {
    if (family === "hand") for (const o of [0, 3]) wanted.push(["hand", name, o]);
    else {
      for (const o of [0, 1, 2, 3]) wanted.push(["flat", name, o]);
      if (name === BACK) for (const o of [0, 1, 2, 3]) wanted.push(["stand", name, o]);
    }
  }
  const MAX = 2048;
  const GAP = 1;
  const items: Item[] = [];
  let x = 0;
  let y = 0;
  let row = 0;
  let width = 0;
  for (const [kind, name, o] of wanted) {
    const b = spriteBox(kind, o, deg);
    const cw = Math.ceil(w * b.width);
    const ch = Math.ceil(w * b.height);
    if (x + cw > MAX) {
      x = 0;
      y += row + GAP;
      row = 0;
    }
    items.push([`${kind}:${name}:${o}`, x, y, cw, ch]);
    x += cw + GAP;
    width = Math.max(width, x);
    row = Math.max(row, ch);
  }
  return { width, height: y + row, items };
}

/** Draws a family's sheet laid out by `layout` onto `ctx`. */
export function drawSheet(ctx: Ctx, f: Faces, w: number, deg: number, items: readonly Item[]): void {
  ctx.imageSmoothingQuality = "high";
  for (const [key, x, y, cw, ch] of items) {
    const [kind, name, os] = key.split(":") as [Kind, string, string];
    const o = Number(os);
    ctx.save();
    ctx.translate(x, y);
    ctx.beginPath();
    ctx.rect(0, 0, cw, ch);
    ctx.clip();
    if (kind === "hand") drawHand(ctx, w, f, name, o);
    else drawSolid(ctx, w, ch, f, name, kind, o, deg);
    ctx.restore();
  }
}

/** Bump when the drawing above changes, so cached sheets are drawn again. */
const DRAWING_VERSION = 2; // 2: the shadows fall to the lower right

/** Where finished sheets are kept between visits (the Cache Storage), and a sheet's key there. */
export const SHEET_CACHE = "mj-sprites";
export const sheetKey = (origin: string, family: Family, w: number, deg: number, faces: number) =>
  `${origin}/__sprites/${ART_SOURCE}-${THEME_ID}-${DRAWING_VERSION}/${family}/${w}/${deg}/${faces}`;
